/**
 * @file UserNativeLanguageCell.tsx
 * @brief Native-language cell for the Super Admin Users list (flag + label pill).
 *
 * @details
 * Soft filled pill: circular flagcdn flag via `LanguageFlag` + muted label.
 * Codes come from `users.native_language` (ISO 639 base, e.g. `ur` / `en`).
 *
 * @dependencies
 * - components/ui/LanguageFlag
 * - shared/data/language-flags
 *
 * @references
 * - FRONTEND/src/shared/components/VZUIKit/VZPickers.tsx (VZLanguagePicker)
 * - docs/product/native-language-plan.md
 *
 * @author DES
 * @date 13-07-2026
 */

import { LanguageFlag } from "@/components/ui/LanguageFlag";

function nativeLanguageLabel(code: string): string {
  try {
    const name = new Intl.DisplayNames(["en"], { type: "language" }).of(
      code.toLowerCase(),
    );
    if (name) return name;
  } catch {
    // Unknown / invalid BCP-47 → fall through to code.
  }
  return code.toUpperCase();
}

export function UserNativeLanguageCell({
  code,
}: {
  code: string | null | undefined;
}) {
  const trimmed = code?.trim();
  if (!trimmed) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const language = { code: trimmed.toLowerCase() };

  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/60 py-0.5 pl-0.5 pr-2.5 text-xs text-muted-foreground">
      <LanguageFlag language={language} size="sm" />
      <span className="truncate">{nativeLanguageLabel(trimmed)}</span>
    </span>
  );
}
